'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, Play, Trophy, Zap, Star, Users, Shield } from 'lucide-react';

const floatingCards = [
  { id: 'fc-1', icon: Trophy, label: 'Quiz Champion', value: '+₹2,500', color: '#F59E0B', top: '15%', left: '5%' },
  { id: 'fc-2', icon: Zap, label: 'Streak: 14 days', value: '🔥', color: '#F43F5E', top: '60%', left: '2%' },
  { id: 'fc-3', icon: Star, label: 'Leaderboard #3', value: 'Gold', color: '#A78BFA', top: '20%', right: '5%' },
  { id: 'fc-4', icon: Users, label: '1v1 Challenge', value: 'LIVE', color: '#06B6D4', top: '65%', right: '3%' },
];

export default function HeroSection() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-16 hero-grid-bg overflow-hidden">
      {/* Background glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(circle, #7C3AED, transparent)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-15 blur-3xl"
          style={{ background: 'radial-gradient(circle, #06B6D4, transparent)' }} />
      </div>
      {/* Floating cards */}
      {mounted && floatingCards?.map((card, i) => (
        <div
          key={card?.id}
          className="absolute hidden xl:flex items-center gap-3 px-4 py-3 rounded-xl animate-float"
          style={{
            top: card?.top,
            left: card?.left,
            right: card?.right,
            background: 'rgba(17, 24, 39, 0.9)',
            border: `1px solid ${card?.color}40`,
            backdropFilter: 'blur(8px)',
            animationDelay: `${i * 1.5}s`,
            boxShadow: `0 4px 20px ${card?.color}20`,
          }}
        >
          <card.icon size={18} style={{ color: card?.color }} />
          <div>
            <p className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>{card?.label}</p>
            <p className="text-sm font-bold" style={{ color: card?.color }}>{card?.value}</p>
          </div>
        </div>
      ))}
      {/* Hero content */}
      <div className="relative z-10 text-center max-w-5xl mx-auto px-4">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8"
          style={{ background: 'rgba(124, 58, 237, 0.15)', border: '1px solid rgba(124, 58, 237, 0.3)' }}>
          <span className="status-dot-live" />
          <span className="text-sm font-semibold" style={{ color: '#A78BFA' }}>500+ Live Competitions Right Now</span>
        </div>

        <h1 className="font-extrabold leading-tight mb-6" style={{ fontSize: 'clamp(40px, 7vw, 80px)', letterSpacing: '-0.03em' }}>
          <span className="text-foreground">Play. Learn.</span>
          <br />
          <span className="text-gradient-primary">Challenge. Earn.</span>
        </h1>

        <p className="text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          Brain Basket is the world's most comprehensive skill gaming and competition platform.
          500+ ways to compete, grow, and earn — built for every profession, every skill level.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
          <Link href="/sign-up-login-screen"
            className="btn-primary text-base py-3 px-8 glow-violet">
            Explore 500+ Opportunities
            <ArrowRight size={18} />
          </Link>
          <Link href="/discover-screen"
            className="btn-ghost text-base py-3 px-8 flex items-center gap-2">
            <Play size={16} style={{ color: '#06B6D4' }} />
            Watch How It Works
          </Link>
        </div>

        {/* Trust indicators */}
        <div className="flex flex-wrap items-center justify-center gap-6 text-sm" style={{ color: 'var(--muted-foreground)' }}>
          {[
            { icon: Shield, label: 'Secure & Compliant' },
            { icon: Users, label: '2.4M+ Players' },
            { icon: Trophy, label: '$8.2M+ Prizes Awarded' },
            { icon: Star, label: '4.8/5 Rating' },
          ]?.map((item) => (
            <div key={`trust-${item?.label}`} className="flex items-center gap-2">
              <item.icon size={14} style={{ color: '#A78BFA' }} />
              <span className="font-medium">{item?.label}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Scroll to explore</span>
        <div className="w-5 h-8 rounded-full border-2 flex items-start justify-center pt-1.5"
          style={{ borderColor: 'var(--muted-foreground)' }}>
          <div className="w-1 h-2 rounded-full animate-bounce" style={{ background: 'var(--primary)' }} />
        </div>
      </div>
    </section>
  );
}